import type { ScoreBreakdown, LeadershipFamilyCode, LeadershipTypeCode } from '@/types/equip360';
import { LEADERSHIP_FAMILIES, LEADERSHIP_TYPES } from '@/types';
import { getMetricPercentage } from '@/utils/scoring';

export function getTierLabel(pct: number): string {
  if (pct >= 85) return 'Elite';
  if (pct >= 70) return 'Strong';
  if (pct >= 50) return 'Developing';
  return 'Emerging';
}

export function getTierChipClass(pct: number): string {
  if (pct >= 85) return 'chip-elite';
  if (pct >= 70) return 'chip-strong';
  if (pct >= 50) return 'chip-developing';
  return 'chip-emerging';
}

export function getTierColor(pct: number): string {
  if (pct >= 85) return 'var(--v2-gold2)';
  if (pct >= 70) return 'var(--v2-green)';
  if (pct >= 50) return 'var(--v2-amber)';
  return 'var(--v2-red)';
}

export function getBarGradient(pct: number): string {
  if (pct >= 70) return 'linear-gradient(90deg, #2D6B52, var(--v2-green))';
  if (pct >= 50) return 'linear-gradient(90deg, #A67D1A, var(--v2-amber))';
  return 'linear-gradient(90deg, #A03333, var(--v2-red))';
}

export const BENCHMARKS = {
  ER: { low: 55, high: 72, label: 'Emotional Readiness' },
  BR: { low: 52, high: 68, label: 'Behavioral Reality' },
  CI: { low: 60, high: 76, label: 'Cultural Influence' },
};

export const EQ_BENCHMARKS: Record<string, number> = {
  SA: 64, SR: 62, M: 64, E: 60, SS: 61,
};

export function computeGapMeter(scores: ScoreBreakdown) {
  const trust = getMetricPercentage(scores.culture.T);
  const selfReg = getMetricPercentage(scores.eq.SR);
  const empathy = getMetricPercentage(scores.eq.E);
  const stressSeverity = Math.min(100, 3 * 8);
  const selfPerception = scores.overall.percentage;
  const teamEstimate = Math.round(
    trust * 0.35 + selfReg * 0.25 + empathy * 0.25 + Math.max(0, 100 - stressSeverity) * 0.15
  );
  const gap = selfPerception - teamEstimate;
  return { selfPerception, teamEstimate, gap };
}

export function getArcDashoffset(pct: number, radius = 52): number {
  const circumference = 2 * Math.PI * radius;
  return circumference - (pct / 100) * circumference;
}

export function getDialDashoffset(pct: number, radius = 104): number {
  const circumference = 2 * Math.PI * radius;
  return circumference - (pct / 100) * circumference;
}

export function getDonutDashoffset(pct: number, radius = 30): number {
  const circumference = 2 * Math.PI * radius;
  return circumference - (pct / 100) * circumference;
}

export interface ROICard {
  icon: string;
  iconClass: string;
  statNum: string;
  statColor: string;
  label: string;
  yourScore: string;
  source: string;
}

export function getROICards(scores: ScoreBreakdown): ROICard[] {
  const empathyPct = getMetricPercentage(scores.eq.E);
  const eqPct = scores.eq.percentage;
  const culturePct = scores.culture.percentage;
  const psPct = getMetricPercentage(scores.culture.PS);

  return [
    {
      icon: '📈',
      iconClass: 'roi-icon-gold',
      statNum: '40%',
      statColor: 'var(--v2-gold2)',
      label: 'Higher performance in coaching, decision-making, and communication for leaders with strong empathy',
      yourScore: `Your Empathy: ${empathyPct}% · Gap to unlock: ${Math.max(0, 75 - empathyPct)} points`,
      source: 'Journal of Leadership Research',
    },
    {
      icon: '🏆',
      iconClass: 'roi-icon-green',
      statNum: '90%',
      statColor: 'var(--v2-green)',
      label: 'of top performers have high emotional intelligence — EQ outpredicts IQ for leadership success',
      yourScore: `Your EQ Overall: ${eqPct}% · Top performers: 80%+`,
      source: 'TalentSmart, 2024',
    },
    {
      icon: '💼',
      iconClass: 'roi-icon-blue',
      statNum: '21%',
      statColor: 'var(--v2-blue2)',
      label: 'Higher profitability reported at companies with deeply engaged employees — driven by culture impact',
      yourScore: `Your Cultural Influence: ${culturePct}% · ${culturePct >= 70 ? 'Already in Strong tier' : 'Room to grow'}`,
      source: 'Gallup State of the Workplace',
    },
    {
      icon: '🔄',
      iconClass: 'roi-icon-amber',
      statNum: '59%',
      statColor: 'var(--v2-amber)',
      label: 'Lower employee turnover in organizations led by emotionally intelligent, culture-focused leaders',
      yourScore: `Your Psych Safety: ${psPct}% · ${psPct >= 70 ? 'Already protecting retention' : 'Growth opportunity'}`,
      source: 'Gallup, 2024',
    },
  ];
}

export function getProgressionData(overallPct: number) {
  const currentTier = getTierLabel(overallPct);
  const nextTierThreshold = overallPct >= 85 ? 100 : overallPct >= 70 ? 85 : overallPct >= 50 ? 70 : 50;
  const nextTier = getTierLabel(nextTierThreshold);
  const pointsToNext = nextTierThreshold - overallPct;
  return { currentTier, nextTier, nextTierThreshold, pointsToNext };
}

export function getLeadershipStory(
  scores: ScoreBreakdown,
  familyCode: LeadershipFamilyCode,
  typeCode: LeadershipTypeCode
): string {
  const family = LEADERSHIP_FAMILIES[familyCode];
  const type = LEADERSHIP_TYPES[typeCode];
  const saPct = getMetricPercentage(scores.eq.SA);
  const srPct = getMetricPercentage(scores.eq.SR);
  const mPct = getMetricPercentage(scores.eq.M);
  const bPct = getMetricPercentage(scores.bed.B);
  const exPct = getMetricPercentage(scores.bed.EX);
  const dPct = getMetricPercentage(scores.bed.D);

  const eqStrength = saPct >= 70 && srPct >= 70 ? 'strong self-awareness and regulation' : saPct >= 70 ? 'strong self-awareness' : srPct >= 70 ? 'strong self-regulation' : 'developing emotional foundations';
  const motivationNote = mPct < 60
    ? `your motivation score (${mPct}%) suggests your drive is more <em>situational than relentless</em>`
    : `your motivation (${mPct}%) provides solid fuel for consistent leadership`;

  let bedStory = `Your B.E.D. profile reveals key patterns. `;
  if (exPct <= 10) {
    bedStory += `Your excuses score (${exPct}%) is an outlier worth examining. That number means you are either radically self-accountable or you are <strong>absorbing blame that is not yours to carry</strong>. `;
  }
  bedStory += `You believe in yourself (${bPct}%) and make ${dPct >= 70 ? 'confident' : 'sound'} decisions (${dPct}%). `;

  return `You lead from <strong>${family.tagline.split(',')[0].toLowerCase()}</strong>. As ${type.name} in the ${family.name} family, your ${eqStrength} gives you ${saPct >= 70 ? 'a rare ability to stay grounded when others fragment' : 'a foundation to build upon'}. But there is a tension in your data: ${motivationNote}.<br><br>${bedStory}The next level of your leadership lives in understanding these patterns. Everything else in this report builds toward that shift.`;
}

export function getScoresConnectorInsight(scores: ScoreBreakdown): string {
  const eqPct = scores.eq.percentage;
  const bedPct = scores.bed.percentage;
  const ciPct = scores.culture.percentage;

  const sorted = [
    { name: 'Cultural Influence', pct: ciPct },
    { name: 'Emotional Readiness', pct: eqPct },
    { name: 'Behavioral Reality', pct: bedPct },
  ].sort((a, b) => b.pct - a.pct);

  const highest = sorted[0];
  const lowest = sorted[sorted.length - 1];

  if (highest.pct - lowest.pct > 15) {
    return `Your ${highest.name} <strong>significantly outpaces</strong> your ${lowest.name}. The way you affect others is more advanced than the internal patterns driving you. That gap is not a flaw — it is your clearest growth signal. Close the internal gap and your external impact <strong>compounds</strong>.`;
  }
  return `Your three layer scores are relatively balanced, showing consistent emotional performance across readiness, behavior, and culture. Your focus should be on elevating <strong>all three together</strong> rather than closing gaps between them.`;
}

export function getBEDStoryInsight(scores: ScoreBreakdown): string {
  const bPct = getMetricPercentage(scores.bed.B);
  const exPct = getMetricPercentage(scores.bed.EX);
  const dPct = getMetricPercentage(scores.bed.D);

  if (exPct <= 10) {
    return `Your B.E.D. profile tells one clear story: <em>you carry too much</em>. Strong beliefs, nearly zero excuses, and mid-range decisions form a combination that describes a leader who holds themselves to an unusually high standard — sometimes so high it creates inertia instead of momentum. The ${exPct}% excuses score is your most important data point in this entire report. Before you optimize anything else, you need to understand whether your accountability is <em>fuel or punishment</em>. One version of that score makes you exceptional. The other makes you exhausted.`;
  }
  if (bPct >= 70 && dPct >= 70) {
    return `Your B.E.D. profile shows alignment: strong beliefs (${bPct}%), sound decisions (${dPct}%), and healthy accountability (${exPct}%). This is a leader whose internal patterns <em>support external results</em>. The growth opportunity is consistency — maintaining these patterns under escalating pressure.`;
  }
  return `Your B.E.D. profile reveals a gap between your beliefs (${bPct}%), excuses (${exPct}%), and decisions (${dPct}%). This combination suggests that your internal narrative and your external actions are not yet fully aligned. The opportunity: bring your decisions closer to the standard your beliefs already hold. That alignment is where <em>decisive leadership</em> lives.`;
}

export function getEQPatternInsight(scores: ScoreBreakdown): string {
  const pillars = [
    { name: 'awareness', pct: getMetricPercentage(scores.eq.SA) },
    { name: 'regulation', pct: getMetricPercentage(scores.eq.SR) },
    { name: 'motivation', pct: getMetricPercentage(scores.eq.M) },
    { name: 'empathy', pct: getMetricPercentage(scores.eq.E) },
    { name: 'social skill', pct: getMetricPercentage(scores.eq.SS) },
  ];
  const above70 = pillars.filter((p) => p.pct >= 70);
  const below60 = pillars.filter((p) => p.pct < 60);

  if (above70.length >= 2 && below60.length > 0) {
    const strongStr = above70.map((p) => p.name).join(' and ');
    return `<strong>Your EQ Pattern:</strong> Your ${strongStr} sit above 70% — that is your foundation. Your ${below60.map((p) => p.name).join(', ')} ${below60.length === 1 ? 'needs' : 'need'} attention. This is a leader who is more <strong>consistent than inspiring</strong>. The gap between those two things is not a character flaw. It is your most accessible growth opportunity over the next 90 days.`;
  }
  if (above70.length >= 4) {
    return `<strong>Your EQ Pattern:</strong> Nearly all pillars sit above 70%. Your emotional foundation is strong across the board. Focus now shifts from building skills to <strong>sustaining them under escalating pressure</strong>. That is the difference between Strong and Elite.`;
  }
  return `<strong>Your EQ Pattern:</strong> Your pillars cluster in the developing range, indicating a leader with solid foundations and clear growth vectors. The priority: identify the one pillar that unlocks the others and <strong>focus there first</strong>.`;
}

export function getPressureShadowName(typeCode: LeadershipTypeCode): string {
  const shadows: Record<string, string> = {
    GROUNDED_COMMANDER: 'The Dictator',
    ANCHOR: 'The Immovable',
    STABILIZER: 'The Conflict Avoider',
    RESPONDER: 'The Robot',
    GUARDIAN: 'The Gatekeeper',
    EMPATHIC_STRATEGIST: 'The Absorber',
    BRIDGE_BUILDER: 'The People Pleaser',
    MENTOR: 'The Enabler',
    HARMONIZER: 'The Disappearing Act',
    CULTURAL_ARCHITECT: 'The Corrector',
    CATALYST: 'The Bulldozer',
    ENFORCER: 'The Intimidator',
    OPTIMIZER: 'The Micromanager',
    ACCELERATOR: 'The Burner',
    STANDARD_BEARER: 'The Judge',
    VISIONARY: 'The Dreamer',
    ARCHITECT: 'The Over-Planner',
    ANALYST: 'The Paralyzed',
    NAVIGATOR: 'The Flip-Flopper',
    INTEGRATOR: 'The Indecisive',
  };
  return shadows[typeCode] || 'The Shadow';
}

export function getPressureAntidote(typeCode: LeadershipTypeCode): string {
  const type = LEADERSHIP_TYPES[typeCode];
  const primary = type.blindSpots[0] || '';
  return `When you feel the urge to slip into old patterns, ask one question first: "What does this person need from me right now?" Lead from their need, not your standard. ${primary ? `Watch specifically for the tendency to ${primary.toLowerCase()}.` : ''}`;
}

export function getMTSHeadline(scores: ScoreBreakdown, _typeCode: LeadershipTypeCode): string {
  const exPct = getMetricPercentage(scores.bed.EX);
  const mPct = getMetricPercentage(scores.eq.M);

  if (exPct <= 10 && mPct < 60) return 'Radical Ownership —\nRedefined';
  if (mPct < 50) return 'Reignite Your\nLeadership Drive';
  const lowestScores = [
    { name: 'Self-Awareness', val: scores.eq.SA },
    { name: 'Self-Regulation', val: scores.eq.SR },
    { name: 'Empathy', val: scores.eq.E },
    { name: 'Social Skill', val: scores.eq.SS },
  ].sort((a, b) => a.val - b.val);
  return `Elevate Your\n${lowestScores[0].name}`;
}

export function getActionItems(
  scores: ScoreBreakdown,
  _typeCode: LeadershipTypeCode,
  _familyCode: LeadershipFamilyCode
): Array<{ num: string; headline: string; desc: string; tied: string }> {
  const exPct = getMetricPercentage(scores.bed.EX);
  const mPct = getMetricPercentage(scores.eq.M);
  const ciPct = scores.culture.percentage;
  const ssPct = getMetricPercentage(scores.eq.SS);

  const items = [
    {
      num: '01',
      headline: exPct <= 20 ? 'Own the Outcome — Not the Blame' : 'Strengthen Your Accountability',
      desc: exPct <= 20
        ? `For 7 days, before reacting to any setback, ask: "What does this moment need from me?" Build accountability that energizes rather than depletes.`
        : `For 7 days, track every moment you shift responsibility. Replace "I had to" with "I chose to." Watch how ownership transforms your credibility.`,
      tied: `Excuses ${exPct}% + Motivation ${mPct}%`,
    },
    {
      num: '02',
      headline: 'Audit Your Cultural Footprint',
      desc: `Ask one direct report: "What is one thing I could do to make your work more effective?" Then act on it within 48 hours. Culture is built in the small moments.`,
      tied: `Cultural Influence ${ciPct}% + Blind Spot`,
    },
    {
      num: '03',
      headline: 'Reconnect to Your Core Purpose',
      desc: `Write a 3-sentence "why I lead" statement. Read it every Monday for 30 days. Notice whether your motivation shifts by day 30. Your because is your engine.`,
      tied: `Motivation ${mPct}%`,
    },
    {
      num: '04',
      headline: 'Practice Adaptive Communication',
      desc: `Pick one team member each week. Consciously shift your communication style to match theirs — not yours. Track what shifts in the relationship over four weeks.`,
      tied: `Social Skill ${ssPct}%`,
    },
  ];
  return items;
}
